import React, { useEffect, useRef, useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import placeholder from '../../assets/card_placeholder.png';
import MangaApiService from '../../services/MangaApiService';
import '../animecard/AnimeCardComponent.css';

const MangaCardComponent = memo(({ manga, isInteractive = false, visibilityLabel = null }) => {
    const [state, setState] = useState({
        isHovered: false,
        imageLoaded: false,
        isVisible: false,
        imageSrc: placeholder,
    });
    const cardRef = useRef(null);
    const navigate = useNavigate();
    const hasSession = !!localStorage.getItem('sessionKey');

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setState((prev) => ({ ...prev, isVisible: true }));
                    observer.unobserve(cardRef.current);
                }
            },
            { threshold: 0.01, rootMargin: '0px' }
        );

        if (cardRef.current) {
            observer.observe(cardRef.current);
        }

        return () => {
            if (cardRef.current) {
                observer.unobserve(cardRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!state.isVisible) {
            return;
        }
        let isActive = true;
        const loadImage = (url, onError) => {
            const img = new Image();
            img.src = url;
            img.onload = () => {
                if (!isActive) return;
                setState((prev) => ({
                    ...prev,
                    imageSrc: url,
                    imageLoaded: true,
                }));
            };
            img.onerror = () => {
                if (!isActive) return;
                if (onError) {
                    onError();
                    return;
                }
                setState((prev) => ({
                    ...prev,
                    imageLoaded: true,
                }));
            };
        };
        const loadCover = async () => {
            const coverUrl = MangaApiService.resolveCoverUrl(manga);
            if (!coverUrl) {
                try {
                    const resolved = await MangaApiService.resolveCoverUrlAsync(manga);
                    if (!isActive) {
                        return;
                    }
                    if (!resolved) {
                        setState((prev) => ({ ...prev, imageLoaded: true }));
                        return;
                    }
                    loadImage(resolved);
                } catch (error) {
                    if (!isActive) return;
                    setState((prev) => ({ ...prev, imageLoaded: true }));
                }
                return;
            }
            loadImage(coverUrl, async () => {
                try {
                    const resolved = await MangaApiService.resolveCoverUrlAsync(manga);
                    if (!isActive) {
                        return;
                    }
                    if (!resolved || resolved === coverUrl) {
                        setState((prev) => ({ ...prev, imageLoaded: true }));
                        return;
                    }
                    loadImage(resolved);
                } catch (error) {
                    if (!isActive) return;
                    setState((prev) => ({ ...prev, imageLoaded: true }));
                }
            });
        };
        loadCover();
        return () => {
            isActive = false;
        };
    }, [manga, state.isVisible]);

    if (!manga) {
        return null;
    }

    const title = manga.title || manga.name || manga.slug || 'Без названия';
    const description = manga.description || manga.summary || '';
    const shortDescription = description.length > 80 ? `${description.slice(0, 80)}...` : description;
    const slug = manga.slug || manga.id;

    const handleNavigate = (event) => {
        event.preventDefault();
        if (!slug) {
            return;
        }
        navigate(`/manga/${slug}`);
    };

    const { isHovered, imageLoaded, imageSrc, isVisible } = state;
    const isNsfw = Boolean(manga?.nsfw ?? manga?.isNsfw ?? manga?.is_nsfw);
    const score = Number(manga?.reviewsCount) > 0 && Number(manga?.avgRating) > 0 ? Number(manga.avgRating) : null;

    return (
        <a
            className="anime-card"
            href={slug ? `/manga/${slug}` : '#'}
            ref={cardRef}
            onClick={handleNavigate}
            onMouseEnter={() => setState((prev) => ({ ...prev, isHovered: true }))}
            onMouseLeave={() => setState((prev) => ({ ...prev, isHovered: false }))}
            style={{
                willChange: 'transform, box-shadow',
                transform: isHovered ? 'translateY(5px)' : 'translateY(0)',
            }}
        >
            <div
                className="anime-card-bg"
                style={{ backgroundImage: `url(${imageSrc})` }}
                aria-hidden="true"
            />
            {isVisible && (
                <img
                    src={imageSrc}
                    alt={title}
                    loading="lazy"
                    className={`anime-thumbnail ${isHovered ? 'hovered' : ''} ${imageLoaded ? 'loaded' : ''}`}
                    style={{ willChange: 'opacity, transform' }}
                />
            )}
            {(!imageLoaded || !isVisible) && (
                <div className="anime-card-shimmer" aria-hidden="true" />
            )}
            {visibilityLabel || isNsfw || score ? (
                <div className="badges">
                    {visibilityLabel ? <span className="visibility-badge">{visibilityLabel}</span> : null}
                    {isNsfw ? <span className="visibility-badge manga-tag-nsfw">18+</span> : null}
                    {score ? (
                        <h4 className={`rating ${score >= 9 ? 'premium' : score >= 7.5 ? 'good' : ''}`}>
                            ★ {Number(score.toFixed(1))}
                        </h4>
                    ) : null}
                </div>
            ) : null}
            {isInteractive && (
                <div className="anime-info">
                    <div className="anime-text-container">
                        <h3 className="anime-title-card">{title}</h3>
                        <p className="anime-description">{shortDescription}</p>
                    </div>
                    {hasSession ? (
                        <button className="button-sec watch-button" onClick={handleNavigate}>
                            Читать
                        </button>
                    ) : null}
                </div>
            )}
        </a>
    );
});

export default MangaCardComponent;
